import React, { useMemo } from 'react';
import { Member, PointsHistory } from '../types';
import { TrendingUp, TrendingDown, Calendar, Award, Gift, AlertCircle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface PointsHistoryViewProps {
    member: Member;
}

export const PointsHistoryView: React.FC<PointsHistoryViewProps> = ({ member }) => {
    const history = useMemo(() => {
        const h = member.pointsHistory || [];
        // Sort by timestamp descending (newest first)
        return [...h].sort((a, b) => b.timestamp - a.timestamp);
    }, [member.pointsHistory]);

    const stats = useMemo(() => {
        const h = member.pointsHistory || [];
        const totalEarned = h.filter(item => item.amount > 0).reduce((sum, item) => sum + item.amount, 0);
        const totalSpent = Math.abs(h.filter(item => item.amount < 0).reduce((sum, item) => sum + item.amount, 0));

        return { totalEarned, totalSpent };
    }, [member.pointsHistory]);

    const getTypeIcon = (type: PointsHistory['type']) => {
        switch (type) {
            case 'checkin':
                return <CheckCircle size={18} />;
            case 'manual':
                return <Award size={18} />;
            case 'settlement':
                return <AlertCircle size={18} />;
            case 'redemption':
                return <Gift size={18} />;
            default:
                return <Calendar size={18} />;
        }
    };

    const getTypeLabel = (type: PointsHistory['type']) => {
        switch (type) {
            case 'checkin':
                return '打卡奖励';
            case 'manual':
                return '手动调整';
            case 'settlement':
                return '结算扣除';
            case 'redemption':
                return '兑换消费';
            default:
                return '其他';
        }
    };

    const getTypeColor = (type: PointsHistory['type'], amount: number) => {
        if (amount > 0) {
            return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
        } else {
            return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
        }
    };

    return (
        <div className="space-y-4 pb-20 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-slate-800 dark:text-white">积分历史</h1>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-amber-400 to-yellow-500 dark:from-amber-600 dark:to-yellow-700 p-4 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                        <Award size={20} className="text-white/80" />
                    </div>
                    <p className="text-2xl font-bold text-white">{member.totalPoints || 0}</p>
                    <p className="text-xs text-white/80 mt-1">当前积分</p>
                </div>

                <div className="bg-gradient-to-br from-green-400 to-emerald-500 dark:from-green-600 dark:to-emerald-700 p-4 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                        <TrendingUp size={20} className="text-white/80" />
                    </div>
                    <p className="text-2xl font-bold text-white">{stats.totalEarned}</p>
                    <p className="text-xs text-white/80 mt-1">累计获得</p>
                </div>

                <div className="bg-gradient-to-br from-red-400 to-rose-500 dark:from-red-600 dark:to-rose-700 p-4 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                        <TrendingDown size={20} className="text-white/80" />
                    </div>
                    <p className="text-2xl font-bold text-white">{stats.totalSpent}</p>
                    <p className="text-xs text-white/80 mt-1">累计消耗</p>
                </div>
            </div>

            {/* History List */}
            <div className="space-y-3">
                <h2 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2">
                    变动记录
                </h2>

                {history.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                        <Calendar size={48} className="mx-auto mb-3 opacity-20" />
                        <p>暂无积分变动记录</p>
                    </div>
                ) : (
                    history.map((item) => (
                        <div
                            key={item.id}
                            className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start gap-3">
                                {/* Icon */}
                                <div className={`p-2 rounded-lg ${getTypeColor(item.type, item.amount)}`}>
                                    {getTypeIcon(item.type)}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-slate-800 dark:text-white text-sm">
                                                {getTypeLabel(item.type)}
                                            </p>
                                            {item.habitName && (
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {item.habitName}
                                                </p>
                                            )}
                                            {item.reason && (
                                                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                                                    {item.reason}
                                                </p>
                                            )}
                                        </div>

                                        {/* Amount */}
                                        <div className="text-right flex-shrink-0">
                                            <p className={`text-lg font-bold ${item.amount > 0
                                                    ? 'text-green-600 dark:text-green-400'
                                                    : 'text-red-600 dark:text-red-400'
                                                }`}>
                                                {item.amount > 0 ? '+' : ''}{item.amount}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Date */}
                                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                                        <Calendar size={12} />
                                        <span>{format(new Date(item.timestamp), 'yyyy-MM-dd HH:mm')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
